import React, { useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    MenuItem,
    InputAdornment,
    Chip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const CreatePinModal = ({ open, onClose, boards = [], onCreate }) => {
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [link, setLink] = useState('');
    const [board, setBoard] = useState('');
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const fileInputRef = useRef();

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleTagAdd = (e) => {
        e.preventDefault();
        if (tagInput.trim() && !tags.includes(tagInput.trim())) {
            setTags([...tags, tagInput.trim()]);
            setTagInput('');
        }
    };

    const handleTagDelete = (tagToDelete) => {
        setTags(tags.filter(tag => tag !== tagToDelete));
    };

    const handleSave = () => {
        // Call onCreate with all fields
        onCreate({ image, title, description, link, board, tags, products: [] });
        onClose();
    };

    const handleSaveFromUrl = () => {
        // Stub: implement as needed
        alert('Save from URL not implemented yet.');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Create Pin</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
                    {/* Left: Image upload */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            minWidth: 320,
                            maxWidth: 400
                        }}
                    >
                        <Box
                            onDrop={handleDrop}
                            onDragOver={e => e.preventDefault()}
                            sx={{
                                width: 320,
                                height: 320,
                                border: '2px dashed #ccc',
                                borderRadius: 4,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2,
                                background: '#fafafa',
                                cursor: 'pointer',
                                position: 'relative'
                            }}
                            onClick={() => fileInputRef.current.click()}
                        >
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleImageChange}
                            />
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }}
                                />
                            ) : (
                                <>
                                    <CloudUploadIcon sx={{ fontSize: 40, mb: 1, color: '#888' }} />
                                    <Typography align="center">
                                        Choose a file or drag and drop<br />it here
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                                        We recommend using high quality .jpg files less than 20 MB or .mp4 files less than 200 MB.
                                    </Typography>
                                </>
                            )}
                        </Box>
                        <Button
                            variant="outlined"
                            fullWidth
                            sx={{ mt: 1, mb: 2 }}
                            onClick={handleSaveFromUrl}
                        >
                            Save from URL
                        </Button>
                    </Box>

                    {/* Right: Form fields */}
                    <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Title"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Link"
                            value={link}
                            onChange={e => setLink(e.target.value)}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            select
                            label="Board"
                            value={board}
                            onChange={e => setBoard(e.target.value)}
                            fullWidth
                            sx={{ mb: 2 }}
                        >
                            {boards.map(b => (
                                <MenuItem key={b._id || b.name} value={b._id || b.name}>
                                    {b.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Search for a tag"
                            value={tagInput}
                            onChange={e => setTagInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleTagAdd(e)}
                            fullWidth
                            sx={{ mb: 1 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button size="small" onClick={handleTagAdd}>Add</Button>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {tags.map(tag => (
                                <Chip key={tag} label={tag} onDelete={() => handleTagDelete(tag)} />
                            ))}
                        </Box>
                        <Button variant="outlined" disabled fullWidth sx={{ mb: 2 }}>
                            Add products (coming soon)
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Create</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreatePinModal; 